import { automaticRecoveryBlocked } from "@/auth/recovery";
import { tokenStore } from "@/auth/tokenStore";
import { SECURITY_BASE } from "./endpoints";

// Phase 1 rotating-refresh client.
//
//  - POST /security/v1/refresh with the HttpOnly `pw_refresh` cookie rotates the
//    session and returns a short-TTL access JWT. Requires the non-HttpOnly
//    `pw_csrf` cookie to be echoed in `X-Csrf-Token` (double-submit CSRF).
//  - POST /security/v1/refresh with a valid `Authorization: Bearer` access JWT
//    (bootstrap) mints the initial refresh session and returns a short-TTL token.
//  - All calls use `credentials: "include"` so the browser stores / sends the
//    refresh cookie. Single-flight: concurrent 401s share one in-flight call.

const REFRESH_CACHE_KEY = "auth:token-refreshed";

function csrfCookieName(): string {
  const configured = import.meta.env.VITE_REFRESH_CSRF_COOKIE as
    string | undefined;
  if (configured) return configured;
  try {
    const host = new URL(import.meta.env.VITE_PW_SERVER as string).hostname;
    if (host === "localhost" || host === "127.0.0.1") return "pw_csrf_dev";
  } catch {
    // Fall back to the mode-specific hosted environment below.
  }
  if (import.meta.env.MODE === "staging") return "pw_csrf_staging";
  if (import.meta.env.MODE === "qa") return "pw_csrf_qa";
  return import.meta.env.PROD ? "pw_csrf" : "pw_csrf_qa";
}

const CSRF_COOKIE_NAME = csrfCookieName();
let csrfToken: string | null = null;

function readCookie(name: string): string {
  const prefix = `${name}=`;
  for (const part of document.cookie.split(";")) {
    const trimmed = part.trim();
    if (trimmed.startsWith(prefix)) {
      return trimmed.slice(prefix.length);
    }
  }
  return "";
}

export async function csrfHeaders(): Promise<Record<string, string>> {
  {
    const response = await fetch(`${SECURITY_BASE}/csrf`, {
      credentials: "include",
      signal: AbortSignal.timeout(15_000),
      headers: { Accept: "application/json" },
    });
    if (!response.ok)
      throw new Error("Unable to validate the session request.");
    const body = (await response.json()) as { csrfToken?: string };
    csrfToken = body.csrfToken || readCookie(CSRF_COOKIE_NAME);
  }
  return { "X-Csrf-Token": csrfToken || "" };
}

function dispatchRefreshed(token: string): void {
  window.dispatchEvent(
    new CustomEvent(REFRESH_CACHE_KEY, { detail: { token } }),
  );
}

/**
 * Single-flight silent refresh using the HttpOnly cookie.
 * Resolves to the new access token (already pushed to tokenStore) or null.
 */
async function rotateWithCookie(): Promise<string | null> {
  if (automaticRecoveryBlocked()) return null;
  const generation = tokenStore.generation();
  try {
    const csrf = await csrfHeaders();
    if (!csrf["X-Csrf-Token"]) return null;
    // Identical cookie generations produce the same retry identifier, even
    // when tabs cannot use Web Locks or persistent browser storage.
    const digest = new Uint8Array(
      await crypto.subtle.digest(
        "SHA-256",
        new TextEncoder().encode(csrf["X-Csrf-Token"]),
      ),
    );
    const hex = Array.from(digest.slice(0, 16), (byte) =>
      byte.toString(16).padStart(2, "0"),
    ).join("");
    const rotationId = `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
    const response = await fetch(`${SECURITY_BASE}/refresh`, {
      method: "POST",
      credentials: "include",
      signal: AbortSignal.timeout(15_000),
      headers: {
        ...csrf,
        "X-Refresh-Request-Id": rotationId,
        "Content-Type": "application/json",
      },
    });
    if (!response.ok) {
      if (response.status === 401 && tokenStore.generation() === generation) {
        window.dispatchEvent(new Event("auth:session-expired"));
      }
      return null;
    }
    const body = (await response.json()) as {
      token?: string;
      csrfToken?: string;
    };
    csrfToken = body.csrfToken || null;
    if (!body.token) return null;
    try {
      localStorage.removeItem("pw-refresh-request");
    } catch {
      // Storage restrictions must not discard a successful cookie rotation.
    }
    if (tokenStore.generation() !== generation) return null;
    dispatchRefreshed(body.token);
    if (tokenStore.get() !== body.token) tokenStore.set(body.token);
    return body.token;
  } catch {
    return null;
  }
}

// Single-flight wrapper.
let inFlight: Promise<string | null> | null = null;
export function withAuthCookieLock<T>(operation: () => Promise<T>): Promise<T> {
  return navigator.locks
    ? navigator.locks.request("pw-cms-refresh", operation)
    : operation();
}

export function silentRefresh(): Promise<string | null> {
  if (!inFlight) {
    // Cookies are shared by tabs, but an in-module promise is not. Serialize
    // the whole read/rotate/write operation across same-origin tabs.
    const operation = withAuthCookieLock(() => rotateWithCookie());
    inFlight = operation.finally(() => {
      inFlight = null;
    });
  }
  return inFlight;
}

/**
 * Bootstrap: exchange a freshly-issued access JWT (e.g. from login / OTP) for a
 * refresh session. The server sets refresh/CSRF cookies and returns a short-TTL
 * access token. The caller publishes it only after the exchange succeeds.
 */
export async function bootstrapRefresh(
  accessToken: string,
): Promise<string | null> {
  const generation = tokenStore.generation();
  try {
    return await withAuthCookieLock(async () => {
      const response = await fetch(`${SECURITY_BASE}/refresh`, {
        method: "POST",
        credentials: "include",
        signal: AbortSignal.timeout(15_000),
        headers: {
          Authorization: `Bearer ${accessToken}`,
          "X-Refresh-Bootstrap": "true",
          "Content-Type": "application/json",
        },
      });
      if (!response.ok) return null;
      const body = (await response.json()) as {
        token?: string;
        csrfToken?: string;
      };
      csrfToken = body.csrfToken || null;
      if (!body.token) return null;
      if (tokenStore.generation() !== generation) {
        return null;
      }
      return body.token;
    });
  } catch {
    return null;
  }
}
